import { Injectable } from '@angular/core';
import { ApiService } from './api.service';
import { UserService } from './user.service';
import { PopupService } from './popup.service';
import { Observable } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { catchError, map, tap } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { environment } from '../../environments/environment';
import { select, Store } from '@ngrx/store';
import { selectAuthUser, selectToken } from '../state/auth/auth.selectors';
import {
  selectHubspotCallbackSuccess,
  selectIsConnectedToHubspot,
} from '../state/oauth/oauth.selectors';

@Injectable({
  providedIn: 'root',
})
export class HubspotService {
  private apiUrl = environment.api.url;

  token: string;
  constructor(
    private store: Store,
    private http: HttpClient,
    private authService: AuthService
  ) {}

  authorize() {
    const url = `${this.apiUrl}/oauth/hubspot`;

    this.authService.token().subscribe((token) => {
      this.token = token;
    });

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'Accept-Language': 'en',
        Authorization: `Bearer ${this.token}`, // Use the token here
      }),
    };

    return this.http.get<any>(url, httpOptions).pipe(
      map((response) => {
        if (!response) {
          // If the token is not present in the response, throw an error
          throw new Error('Login failed: No token received');
        }

        return {
          url: response.data,
        };
      }),
      catchError((error) => {
        throw error;
      })
    );
  }

  callback(code, user_id) {
    const url = `${this.apiUrl}/oauth/hubspot/callback`;

    this.authService.token().subscribe((token) => {
      this.token = token;
    });

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'Accept-Language': 'en',
        Authorization: `Bearer ${this.token}`, // Use the token here
      }),
    };

    return this.http.post<any>(
      url,
      { code: code, user_id: user_id },
      httpOptions
    );
  }

  logout(user_id: number) {
    const url = `${this.apiUrl}/oauth/hubspot/disconnect`;

    this.authService.token().subscribe((token) => {
      this.token = token;
    });

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'Accept-Language': 'en',
        Authorization: `Bearer ${this.token}`, // Use the token here
      }),
    };

    return this.http.post<any>(url, { user_id: user_id }, httpOptions);
  }

  newDeal(user_id: number, properties: any, contact_id: number) {
    const url = `${this.apiUrl}/oauth/hubspot/new/deal`;

    this.authService.token().subscribe((token) => {
      this.token = token;
    });

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'Accept-Language': 'en',
        Authorization: `Bearer ${this.token}`, // Use the token here
      }),
    };

    return this.http.post<any>(
      url,
      { user_id: user_id, properties: properties, contact_id: contact_id },
      httpOptions
    );
  }

  newContact(user_id: number, mail: any, company: any, phone: any, name: any) {
    const url = `${this.apiUrl}/oauth/hubspot/new/contact`;

    this.authService.token().subscribe((token) => {
      this.token = token;
    });

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'Accept-Language': 'en',
        Authorization: `Bearer ${this.token}`, // Use the token here
      }),
    };

    return this.http.post<any>(
      url,
      {
        user_id: user_id,
        name: name,
        mail: mail,
        company: company,
        phone: phone,
      },
      httpOptions
    );
  }

  newCompany(
    user_id: any,
    company_name: any,
    company_domain: any,
    company_industry: any,
    company_phone: any,
    company_city: any,
    company_state: any,
    company_life_cycle_stage,
    contact_id
  ) {
    const url = `${this.apiUrl}/oauth/hubspot/new/company`;

    this.authService.token().subscribe((token) => {
      this.token = token;
    });

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'Accept-Language': 'en',
        Authorization: `Bearer ${this.token}`, // Use the token here
      }),
    };

    return this.http.post<any>(
      url,
      {
        user_id: user_id,
        company_name: company_name,
        company_domain: company_domain,
        company_industry: company_industry,
        company_phone: company_phone,
        company_city: company_city,
        company_state: company_state,
        company_life_cycle_stage: company_life_cycle_stage,
        contact_id: contact_id,
      },
      httpOptions
    );
  }

  showDeals(contact_id, id) {
    const url =
      `${this.apiUrl}/oauth/hubspot/deals?search_id=` +
      id +
      `&page=` +
      1 +
      `&contact_id=` +
      contact_id;

    this.authService.token().subscribe((token) => {
      this.token = token;
    });

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'Accept-Language': 'en',
        Authorization: `Bearer ${this.token}`, // Use the token here
      }),
    };

    return this.http.get<any>(url, httpOptions);
  }

  showMoreDeals(contact_id, id, page) {
    const url =
      `${this.apiUrl}/oauth/hubspot/deals?search_id=` +
      id +
      `&page=` +
      page +
      `&contact_id=` +
      contact_id;

    this.authService.token().subscribe((token) => {
      this.token = token;
    });

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'Accept-Language': 'en',
        Authorization: `Bearer ${this.token}`, // Use the token here
      }),
    };

    return this.http.get<any>(url, httpOptions);
  }

  showCompany(contact_id, id) {
    const url =
      `${this.apiUrl}/oauth/hubspot/company?search_id=` +
      id +
      '&contact_id=' +
      contact_id;

    this.authService.token().subscribe((token) => {
      this.token = token;
    });

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'Accept-Language': 'en',
        Authorization: `Bearer ${this.token}`, // Use the token here
      }),
    };

    return this.http.get<any>(url, httpOptions);
  }

  getHubspotCompanies(id) {
    const url = `${this.apiUrl}/oauth/hubspot/companies?search_id=` + id;

    this.authService.token().subscribe((token) => {
      this.token = token;
    });

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'Accept-Language': 'en',
        Authorization: `Bearer ${this.token}`, // Use the token here
      }),
    };

    return this.http.get<any>(url, httpOptions);
  }

  getHubspotCompanyLifeCycleStages(id) {
    const url =
      `${this.apiUrl}/oauth/hubspot/company/life_cycle_stages?search_id=` + id;

    this.authService.token().subscribe((token) => {
      this.token = token;
    });

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'Accept-Language': 'en',
        Authorization: `Bearer ${this.token}`, // Use the token here
      }),
    };

    return this.http.get<any>(url, httpOptions);
  }

  getHubspotCompanyIndustries(id) {
    const url =
      `${this.apiUrl}/oauth/hubspot/company/industries?search_id=` + id;

    this.authService.token().subscribe((token) => {
      this.token = token;
    });

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'Accept-Language': 'en',
        Authorization: `Bearer ${this.token}`, // Use the token here
      }),
    };

    return this.http.get<any>(url, httpOptions);
  }

  getHubspotDealOwners(id) {
    const url = `${this.apiUrl}/oauth/hubspot/deal/owners?search_id=` + id;

    this.authService.token().subscribe((token) => {
      this.token = token;
    });

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'Accept-Language': 'en',
        Authorization: `Bearer ${this.token}`, // Use the token here
      }),
    };

    return this.http.get<any>(url, httpOptions);
  }

  getHubspotDealPipelines(id) {
    const url = `${this.apiUrl}/oauth/hubspot/deal/pipelines?search_id=` + id;

    this.authService.token().subscribe((token) => {
      this.token = token;
    });

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'Accept-Language': 'en',
        Authorization: `Bearer ${this.token}`, // Use the token here
      }),
    };

    return this.http.get<any>(url, httpOptions);
  }

  getHubspotDealStages(id, pipeline) {
    const url =
      `${this.apiUrl}/oauth/hubspot/deal/stages?search_id=` +
      id +
      `&pipeline_label=` +
      pipeline[0] +
      `&pipeline_id=` +
      pipeline[1];

    this.authService.token().subscribe((token) => {
      this.token = token;
    });

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'Accept-Language': 'en',
        Authorization: `Bearer ${this.token}`, // Use the token here
      }),
    };

    return this.http.get<any>(url, httpOptions);
  }

  showContact(id) {
    const url = `${this.apiUrl}/oauth/hubspot/contact?search_id=` + id;

    this.authService.token().subscribe((token) => {
      this.token = token;
    });

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'Accept-Language': 'en',
        Authorization: `Bearer ${this.token}`, // Use the token here
      }),
    };

    return this.http.get<any>(url, httpOptions);
  }

  oauth(): Observable<any | null> {
    return this.store.pipe(select(selectIsConnectedToHubspot));
  }
}
